import {formatCents} from '../../../helpers/bookingFormat';

/**
 * One-sentence example of what a shopper pays for a booking, shown under the
 * seller's "Product price applies per" control. Mirrors
 * server/helpers/booking/pricing.js computeBookingPricing(): the product price
 * times units (1 per appointment or pickup window, attendees, or nights) plus
 * fixed fees once per booking. Appointment length never multiplies the price.
 * See plans/completed/PLAN-BOOKING-SCHEDULING.md Phase 4b item 19.
 *
 * @param {Object} options
 * @param {number} options.price - product price in cents
 * @param {string} [options.currency]
 * @param {string} options.mode - appointment | daterange | pickup
 * @param {string} options.pricingUnit - slot | attendee | night
 * @param {number|string} [options.maxAttendees]
 * @param {number|string} [options.minNights]
 * @param {Array<{amount: string|number}>} [options.fees] - amounts in dollars, as edited
 * @returns {string}
 */
export default function bookingPriceExample({
    price,
    currency = 'USD',
    mode,
    pricingUnit,
    maxAttendees,
    minNights,
    fees = [],
}) {
    const priceCents = Number(price);
    if (price === null || price === undefined || !Number.isFinite(priceCents)) {
        return 'Set the product price on the Basics step to see what shoppers pay.';
    }
    const money = cents => formatCents(cents, currency || 'USD', 'en-US');
    const unitPrice = money(priceCents);

    let sentence;
    if (pricingUnit === 'night') {
        const nights = Math.max(2, parseInt(minNights, 10) || 1);
        sentence = `Shoppers pay ${unitPrice} per night: a ${nights}-night stay costs ${
            money(priceCents * nights)}.`;
    } else if (pricingUnit === 'attendee') {
        sentence = `Shoppers pay ${unitPrice} per attendee: 2 attendees pay ${
            money(priceCents * 2)}.`;
    } else if (mode === 'pickup') {
        sentence = `Shoppers pay ${unitPrice} per pickup window.`;
    } else if ((parseInt(maxAttendees, 10) || 1) > 1) {
        sentence = `Shoppers pay ${unitPrice} per booking, however many attendees `
            + 'and whatever its length.';
    } else {
        sentence = `Shoppers pay ${unitPrice} per appointment, whatever its length.`;
    }

    const feesCents = fees.reduce((sum, fee) => {
        const cents = Math.round(parseFloat(fee?.amount) * 100);
        return Number.isFinite(cents) && cents > 0 ? sum + cents : sum;
    }, 0);
    return feesCents > 0
        ? `${sentence} Fixed fees add ${money(feesCents)} to each booking.`
        : sentence;
}
